import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostBinding,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AccaBoostConfig, BetslipConfig, EpcotConfig, StakeConfig } from '@frontend/sports/common/client-config-data-access';
import { CalculatedOdds, OddsFormatterService, emptyCalculatedOdds } from '@frontend/sports/odds/feature';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { Store, createSelector } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { isAccaBoostTokenSelector } from 'packages/sports/web/app/src/acca-boost-base/acca-boost-utils';
import { NumpadDisplayService } from 'packages/sports/web/app/src/numpad/numpad-display.service';
import { Observable, Subscription, combineLatest, map } from 'rxjs';

import { IBetslipMessages } from '../../../../base/models/betslip-messages';
import {
    BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
    IBetslipMessagesProviderService,
} from '../../../../base/services/betslip-messages-provider.service';
import { selectIsLinearBetslip } from '../../../../base/store/selectors';
import { IBetslipRootState } from '../../../../base/store/state';
import { BetslipType } from '../../../../core/betslip-type';
import { pickIdFactory } from '../../../../core/picks/pick-id';
import { BetslipGeneratorContextType, IBetslip } from '../../../bet-generation/models';
import { oddsBoostAnimations } from '../../../reward-tokens/components/digital/odds-boost-animations';
import { RewardTokenContext } from '../../../reward-tokens/reward-tokens.model';
import { selectMultiSingleTokenRewardContext, selectRewardTokens } from '../../../reward-tokens/selectors';
import { getSelectedTokenForContext } from '../../../reward-tokens/services/linear-reward-tokens.utils';
import { singleBetPicksSelector } from '../../../single-bet/selectors';
import { BETSLIP_LINEAR_NUMPAD, BETSLIP_SUMMARY_NUMPAD } from '../../../stake/models';
import { parseStakeFieldStake } from '../../../stake/utils';
import { ITaxationInfoViewModel } from '../../../taxation/components/digital/taxation-info.component';
import { BetslipTeaserActions } from '../../../teaser-bet/actions';
import { BetslipTypesActions } from '../../../types/actions';
import { betslipCurrentTypeSelector } from '../../../types/base/selectors';
import { betslipTypeStateSelector } from '../../../types/selectors';
import { IBetslipTypeState } from '../../../types/state';
import { LessStakeAfterTaxPreCheckError } from '../../../validation/errors/pre-check/less-stake-after-tax-pre-check-error';
import { ErrorViewModel, IErrorComponentDisplayConfig } from '../../../validation/models/models';
import { IBetslipSummary } from '../../models';
import { BetslipSummaryService } from '../../services/betslip-summary.service';
import { evaluateClasses } from '../../utils/digital/betslip-summary-classes-utils';
import { IDigitalSummaryViewModel } from './digital-summary-view.model';

type SummaryViewModel = IDigitalSummaryViewModel & {
    taxationVM?: ITaxationInfoViewModel;
    isLinearBetslip: boolean;
    tokenContext?: RewardTokenContext;
    summaryTokenContext?: RewardTokenContext;
    showTokenSummary: boolean;
    showTokenSummaryInStakeRow: boolean;
};

const selectSummaryTokenSelectorRewardContext = createSelector(betslipCurrentTypeSelector, singleBetPicksSelector, (type, picks) => {
    if (!type) {
        return undefined;
    }

    if (type === BetslipType.Combo) {
        return { betslipType: BetslipType.Combo };
    }

    if (type === BetslipType.Teaser) {
        return { betslipType: BetslipType.Teaser };
    }

    if (type !== BetslipType.Single) {
        return undefined;
    }

    const selectedPicks = Object.entries(picks).filter(([_, pick]) => pick.isSelected);
    const isSinglePick = selectedPicks.length === 1;

    if (!isSinglePick) {
        return undefined;
    }

    const [pickId, _] = selectedPicks[0];

    return {
        betslipType: BetslipType.Single,
        pickId: pickIdFactory(pickId),
    };
});

export const selectSummaryRewardToken = createSelector(
    selectSummaryTokenSelectorRewardContext,
    selectRewardTokens,
    betslipTypeStateSelector,
    (context, tokens, types) => (context ? getSelectedTokenForContext(context, tokens, types) : null),
);

@Component({
    selector: 'bs-digital-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss'],
    animations: [oddsBoostAnimations],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export default class SummaryComponent implements OnInit, OnDestroy {
    @HostBinding('class') get className(): string {
        const betslip = this.betslip;
        const betslipSummary = this.betslipSummary;
        const typeState = this.typeState;
        const teaserSummariesLength = this.teaserSummaries.length;

        return evaluateClasses(
            betslip,
            betslipSummary,
            typeState,
            teaserSummariesLength,
            this.isLinearBetslip,
            this.betslipConfig.isSportcastAsComboEnabled,
        ).join(' ');
    }

    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() isTeaser = false;
    @Input() succeededTeaserBetCount = 0;
    @Input() isQuickDepositOpen = false;

    teaserSummaries: IBetslipSummary[] = [];
    isNumpadOpen = false;
    vm$: Observable<SummaryViewModel>;
    taxInfoMessage: string;
    isTurnOverTax: boolean;
    errors: ErrorViewModel[] = [];
    possibleWinningsValue: Decimal | null;

    linearBetslipErrorFilter: IErrorComponentDisplayConfig;

    shouldShowRewardTokens$: Observable<boolean>;
    isTaxationInfoVisible = false;
    isStakeLimitsInfoDisplayed = false;
    messages: {
        stake: string;
        stakePerBet: string;
        bets: string;
        totalStake: string;
        totalOdds: string;
        possibleWinnings: string;
        possibleWinningsNet: string;
        totalReturns: string;
        totalReturnsNet: string;
    };

    sitecore: IBetslipMessages;
    typeState?: IBetslipTypeState;

    readonly rewardToken$ = this.store.select(selectSummaryRewardToken);

    private betslip?: IBetslip;
    private betslipSummary?: IBetslipSummary;

    private isLinearBetslipSubscription: Subscription;
    private isLinearBetslip: boolean;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private summaryService: BetslipSummaryService,
        private changeDetector: ChangeDetectorRef,
        private store: Store<IBetslipRootState>,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private oddsFormatter: OddsFormatterService,
        private stakeConfig: StakeConfig,
        private numpadDisplayService: NumpadDisplayService,
        private userSettings: SyncUserSettingsService,
        private epcotConfig: EpcotConfig,
        public betslipConfig: BetslipConfig,
        private accaBoostConfig: AccaBoostConfig,
    ) {
        this.messages = messageProvider.getMessages().summary;
        this.sitecore = messageProvider.getMessages();
        this.isTurnOverTax = this.userSettings.isTurnoverTaxApplied;

        this.linearBetslipErrorFilter = {
            showOfferChangedError: true,
            showMiniumPicksError: false,
            showComboPreventionError: false,
            betslipErrorsFilter: () => false,
        };
    }

    ngOnInit(): void {
        this.vm$ = this.isTeaser ? this.initTeaserSummary() : this.initDefaultSummary();

        this.isStakeLimitsInfoDisplayed = this.stakeConfig.showStakeLimitInfo;
    }

    private initDefaultSummary(): Observable<SummaryViewModel> {
        const summaryVm = combineLatest([
            this.summaryService.selectSummaryViewModel$(BetslipGeneratorContextType.Betslip),
            this.store.select(betslipTypeStateSelector),
            this.store.select(selectIsLinearBetslip),
            this.store.select(selectSummaryTokenSelectorRewardContext),
            this.store.select(selectMultiSingleTokenRewardContext),
            this.store.select(isAccaBoostTokenSelector),
        ]).pipe(
            map(([{ betslip, summary, vm }, typeState, isLinearBetslip, tokenContext, singleTokenSummaryContext, isAccaBoostToken]) => {
                this.betslip = betslip;
                this.typeState = typeState;
                this.betslipSummary = summary;
                if (this.isTurnOverTax) {
                    this.checkedBetSlipError(this.betslipSummary);
                }

                return this.getSummaryVM(vm, isLinearBetslip, tokenContext, singleTokenSummaryContext, isAccaBoostToken);
            }),
        );

        this.isLinearBetslipSubscription?.unsubscribe();
        this.isLinearBetslipSubscription = this.store
            .select(selectIsLinearBetslip)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                this.isLinearBetslip = value;
            });

        return summaryVm;
    }

    private initTeaserSummary(): Observable<SummaryViewModel> {
        return combineLatest([
            this.summaryService.getTeaserBetSummary$(BetslipGeneratorContextType.Betslip),
            this.store.select(betslipTypeStateSelector),
            this.store.select(selectIsLinearBetslip),
            this.store.select(selectSummaryTokenSelectorRewardContext),
        ]).pipe(
            map(([teaserSummary, typeState, isLinearBetslip, tokenContext]) => {
                this.betslip = teaserSummary[0];
                this.betslipSummary = teaserSummary[1];
                this.typeState = typeState;

                const summary: IDigitalSummaryViewModel = {
                    possibleWinnings: this.betslipSummary.possibleWinnings,
                    possibleWinningsNet: this.betslipSummary.possibleWinnings,
                    totalStake: this.betslipSummary.totalStake,
                    taxation: this.betslipSummary.taxation,
                    totalOdds: this.betslipSummary.totalOdds,
                    userBetCount: this.betslipSummary.betCount,
                    stake: Number(typeState.teaserBet.stake),
                    boostedOdds: null,
                    boostedWinnings: null,
                    winningsBoost: null,
                    isPotentailReturn: false,
                };

                return this.getSummaryVM(summary, isLinearBetslip, tokenContext, undefined, false);
            }),
            takeUntilDestroyed(this.destroyRef),
        );
    }

    checkedBetSlipError(betslipSummary: IBetslipSummary) {
        this.possibleWinningsValue = betslipSummary.possibleWinningsNet ? betslipSummary.possibleWinningsNet : betslipSummary.possibleWinnings;
        const isLessStakeTax =
            !!betslipSummary.slipSummaries && !!betslipSummary.slipSummaries[0].betSummaries[0].freeBetWinnings
                ? betslipSummary.slipSummaries[0].betSummaries[0].freeBetWinnings.lessThan(betslipSummary.totalStake ?? 0)
                : betslipSummary.possibleWinnings?.lessThan(betslipSummary.totalStake ?? 0);

        if (isLessStakeTax) {
            this.errors.push({
                error: new LessStakeAfterTaxPreCheckError(),
                message: this.sitecore.errors.LessStakeAfterTax,
            });
        }
    }

    get isNumpadOpenWhenEpcotEnabled(): boolean {
        return this.epcotConfig.isEnabled && this.numpadDisplayService.isOpen();
    }

    get hideTotalOdds(): boolean {
        return this.typeState?.base.currentSelectedType === BetslipType.Combo && this.typeState.comboBet.isEachWay;
    }

    get keypadId(): string {
        return this.betslipConfig.isLinearKeypadEnabled ? BETSLIP_LINEAR_NUMPAD : BETSLIP_SUMMARY_NUMPAD;
    }

    onStakeChanged(stakeStr: string): void {
        const stake = parseStakeFieldStake(stakeStr);

        if (this.isTeaser) {
            this.store.dispatch(BetslipTeaserActions.setTeaserBetStake({ stake, stakeStr }));

            return;
        }

        this.store.dispatch(BetslipTypesActions.setStake({ stake, stakeStr }));
    }

    displayTaxationInfo(): void {
        this.isTaxationInfoVisible = true;
        this.changeDetector.markForCheck();
    }

    getTaxationViewModel(vm: IDigitalSummaryViewModel): ITaxationInfoViewModel {
        return {
            possibleWinningsGross: vm.possibleWinnings,
            possibleWinningsNet: vm.possibleWinningsNet,
            taxationAmount: vm.taxation?.amount?.greaterThan(0) ? vm.taxation.amount : null,
            taxationRate: vm.taxation?.rate || null,
            winningsBoost: vm.winningsBoost,
            taxationMessage:
                this.isTurnOverTax && !!vm.possibleWinnings
                    ? Number(vm.possibleWinnings) > this.userSettings.configSettings.winningsTaxThreshold
                        ? this.sitecore.common.TurnoverAndWinningTax
                        : this.sitecore.common.TurnoverTax
                    : null,
        };
    }

    formattedOdds(odds: CalculatedOdds | null): string {
        if (odds === emptyCalculatedOdds) {
            return '-';
        }

        return this.oddsFormatter.format(odds, { displayFormat: this.oddsDisplayFormat, isSummary: true });
    }

    private getSummaryVM(
        summary: IDigitalSummaryViewModel,
        isLinearBetslip: boolean,
        tokenContext: RewardTokenContext | undefined,
        singleTokenSummaryContext: RewardTokenContext | undefined,
        isAccaBoostToken: boolean,
    ): SummaryViewModel {
        const summaryTokenContext = tokenContext ?? singleTokenSummaryContext;
        const isAccaBoostEnhanced = this.accaBoostConfig.isEnhancedLayoutEnabled && isAccaBoostToken;
        const shouldShowTokenSummary = !isLinearBetslip && !!summaryTokenContext && !this.epcotConfig.isEnabled;

        return {
            ...summary,
            taxationVM: this.getTaxationViewModel(summary) ?? undefined,
            isLinearBetslip,
            tokenContext,
            summaryTokenContext,
            possibleWinnings: !!summary.possibleWinnings && summary.possibleWinnings.greaterThan(0) ? summary.possibleWinnings : null,
            possibleWinningsNet: !!summary.possibleWinningsNet && summary.possibleWinningsNet.greaterThan(0) ? summary.possibleWinningsNet : null,
            showTokenSummary: shouldShowTokenSummary && !isAccaBoostEnhanced,
            showTokenSummaryInStakeRow: shouldShowTokenSummary && isAccaBoostEnhanced,
        };
    }

    ngOnDestroy(): void {
        this.isLinearBetslipSubscription?.unsubscribe();
    }
}
